/**
 * /extensii-domenii — migrated from public/js/domain-ext.js.
 *
 * Substantial page:
 *  - Filter form with category dropdown, fee slider (noUiSlider), checkboxes,
 *    radio boxes; persisted in `fdata` object
 *  - PriceList render based on filter + sort
 *  - Filter group slide toggle (desktop) / mobile popup
 *  - BULK availability search via /extensii-domenii/search?sld=X (WHMCS)
 *  - FormValidator (Resort form classic)
 *  - Tooltip on info icons
 *  - VDOM helper `El()` for creating DOM elements
 */
import { Resort } from '../core/resort.js';

declare const noUiSlider: {
    create: (target: Element, opts: unknown) => { on: (event: string, cb: (v: number[]) => void) => void };
};

interface ExtInfo {
    categ: string[];
    reg:   Record<number, number>;
    renew: Record<number, number>;
    trans: Record<number, number>;
}
interface ExtList {
    currency: string;
    pricing:  Record<string, ExtInfo>;
}
interface ReqDataEntry { oldPrice?: string; info?: string; }
interface BulkResult {
    domain:    string;
    tld:       string;
    parent?:   string;
    available: boolean;
    register:  number;
    renew:     number;
    transfer:  number;
}

const filterOptions = {
    feeRange: { min: 0, max: 5000 },
    levels: {
        250:  'Basic: annual spending < €250.00',
        500:  'Pro: annual spending < €500.00',
        1000: 'Gold: annual spending < €1000.00',
    } as Record<number, string>,
};

/** Mini VDOM helper — creates element with attrs + children. */
function El(tag: string, attr: Record<string, string | null> = {}, ...children: unknown[]): HTMLElement {
    const elem = document.createElement(tag);
    for (const name in attr) {
        const val = attr[name];
        if (val === null || val === undefined) continue;
        if (name === 'className') { elem.className = val; continue; }
        elem.setAttribute(name, val);
    }
    children.forEach((child) => { if (child) elem.append(child as Node); });
    return elem;
}

new Resort(async (rt) => {
    const elCateg     = $('select[name="categ"]') as HTMLSelectElement | null;
    const elBadge     = $('.search-form .btn-filter .badge') as HTMLElement | null;
    const elTable     = $('.price-table') as HTMLTableElement | null;
    const elTbody     = (elTable?.findChildren('tbody') as HTMLElement | null);
    const elFeeRange  = $('.fee-range') as HTMLElement | null;
    const elFeeVal    = $('.fee-val') as HTMLElement | null;

    if (!elCateg || !elTable || !elTbody || !elFeeRange || !elFeeVal) return;

    interface FilterData {
        sort?:       false | Record<string, 'asc' | 'desc'>;
        query?:      string;
        q_type?:     'in' | 'sw' | 'ew' | 'eq';
        categ?:      string;
        start_fee?:  number;
        end_fee?:    number;
        req?:        'with' | 'without';
        main_tlds?:  boolean;
        promo?:      boolean;
        b_level?:    string;
        dom_type?:   'ccTLDs' | 'gTLDs';
    }
    let fdata: FilterData = { sort: false };

    const raw = $('#ext-list')?.textContent ?? '';
    const extList = JSON.parse(raw) as ExtList;
    const categList: Record<string, boolean> = {};
    const pricingList = Object.entries(extList.pricing);

    for (const [, data] of pricingList) {
        data.categ.forEach((name) => {
            if (!categList[name]) {
                elCateg.add(new Option(name, name));
                categList[name] = true;
            }
        });
    }

    /* Fetch optional XML data (oldPrice + info per TLD) */
    const fetchReqData = async (): Promise<Record<string, ReqDataEntry>> => {
        const response = await fetch(`/res/tlds.xml?${Date.now()}`, { method: 'get', cache: 'no-cache' });
        const xml = new DOMParser().parseFromString(await response.text(), 'text/xml');
        const tlds = xml.getElementsByTagName('tlds')[0];
        if (!tlds) return {};

        const out: Record<string, ReqDataEntry> = {};
        tlds.find('ext', (extNode: Element) => {
            const name = extNode.getAttribute('name');
            if (!name) return true;
            const oldPrice = extNode.getElementsByTagName('oldPrice')[0];
            const info     = extNode.getElementsByTagName('info')[0];
            if (oldPrice || info) out[name] = {};
            if (oldPrice) out[name]!.oldPrice = oldPrice.textContent ?? '';
            if (info)     out[name]!.info     = info.innerHTML;
            return true;
        });
        return out;
    };

    const renderBadge = (count: number): void => {
        if (!elBadge) return;
        elBadge.classList.toggle('hide', !count);
        elBadge.textContent = String(count);
    };

    const reqData = await fetchReqData();
    let filtCount = 0;

    /* Sort comparator */
    type Entry = [string, ExtInfo];
    const sortBy = (sortObj: Record<string, 'asc' | 'desc'>) => {
        const [key, order] = Object.entries(sortObj)[0]!;
        return (a: Entry, b: Entry): number => {
            if (key === 'name') {
                return order === 'asc'
                    ? a[0].localeCompare(b[0], undefined, { sensitivity: 'base' })
                    : b[0].localeCompare(a[0], undefined, { sensitivity: 'base' });
            }
            const objA = a[1][key as keyof ExtInfo] as Record<number, number>;
            const objB = b[1][key as keyof ExtInfo] as Record<number, number>;
            const keyA = Object.keys(objA)[0]!;
            const keyB = Object.keys(objB)[0]!;
            return order === 'asc' ? objA[+keyA]! - objB[+keyB]! : objB[+keyB]! - objA[+keyA]!;
        };
    };

    /* Render pricing list */
    const priceListRender = (): void => {
        if (!raw) return;

        let filtered = pricingList.filter(([ext, extData]) => {
            if (fdata.query) {
                const queryStr = fdata.query.startsWith('.') ? fdata.query.slice(1) : fdata.query;
                const checks = {
                    in: ext.includes(queryStr),
                    sw: ext.startsWith(queryStr),
                    ew: ext.endsWith(queryStr),
                    eq: ext === queryStr,
                };
                if (!checks[fdata.q_type ?? 'in']) return false;
            }
            if (fdata.categ && !extData.categ?.includes(fdata.categ)) return false;
            if (fdata.start_fee !== undefined && fdata.end_fee !== undefined
                && (extData.reg[1]! < fdata.start_fee || extData.reg[1]! > fdata.end_fee)) return false;
            if (fdata.req) {
                if (fdata.req === 'with' && !reqData[ext]?.info) return false;
                if (fdata.req === 'without' && reqData[ext]?.info) return false;
            }
            if (fdata.main_tlds && ext.indexOf('.', 1) !== -1) return false;
            if (fdata.promo && !reqData[ext]?.oldPrice) return false;
            if (fdata.b_level) {
                const lvl = parseInt(fdata.b_level, 10);
                if (lvl && extData.reg[1]! > lvl) return false;
            }
            if (fdata.dom_type) {
                const lastPart = ext.split('.').pop() ?? '';
                if (fdata.dom_type === 'ccTLDs') {
                    if (lastPart.length !== 2) return false;
                } else {
                    if (lastPart.length < 3) return false;
                }
            }
            return true;
        });

        elTable.classList.toggle('hide', !filtered.length);
        elTbody.replaceChildren();

        if (fdata.sort) filtered = filtered.sort(sortBy(fdata.sort));

        for (const [ext, data] of filtered) {
            const keys = Object.keys(data.reg);
            const period: string = keys.length > 1 ? `${keys[0]} to ${keys[keys.length - 1]}` : (keys[0] ?? '');
            const minPeriod = keys[0] === '1' ? ' an' : `${keys[0]}  ani`;

            let elInfo: HTMLElement | string = ' ';
            if (reqData[ext]?.info) {
                elInfo = El('i', { class: 'icon ic-info tooltip', 'data-color': 'green', 'data-tooltip': reqData[ext]!.info! });
                (elInfo as Element).Tooltip();
            }
            const oldPrice = reqData[ext]?.oldPrice;

            elTbody.append(El('tr', {},
                El('td', {},
                    El('a', { href: '#' }, `.${ext}`),
                    El('div', { class: 'reg-price' }, `Înregistrare: ${data.reg[1]} ${extList.currency} / ${minPeriod}`),
                ),
                El('td', {}, elInfo),
                El('td', {}, period),
                El('td', {},
                    oldPrice ? El('span', { class: 'old-price' }, `${oldPrice} ${extList.currency}`) : null,
                    El('span', { class: oldPrice ? 'new-price' : 'price' }, `${data.reg[1]} ${extList.currency}`),
                ),
                El('td', {}, `${data.renew[1]} ${extList.currency}`),
                El('td', {}, `${data.trans[1]} ${extList.currency}`),
            ));
        }
    };

    /* Filter group toggle */
    let isBusy = false;
    const filterGrp = $('.filter-group') as HTMLElement | null;
    if (!filterGrp) return;

    const closePopup = (): void => {
        document.body.style.overflowY = 'auto';
        filterGrp.addEventListener('transitionend', () => { filterGrp.style.removeProperty('display'); }, { once: true });
        setTimeout(() => filterGrp.classList.remove('opened'));
    };

    ($('.search-form .btn-filter') as Element | null)?.on('click', () => {
        if (window.innerWidth >= 1200) {
            if (isBusy) return;
            isBusy = true;
            ($('.search-form .btn-filter .icon') as Element | null)?.classList.toggle('ic-frm-close');
            filterGrp.slideToggle(() => { isBusy = false; });
            return;
        }
        document.body.style.overflowY = 'hidden';
        filterGrp.style.display = 'block';
        setTimeout(() => filterGrp.classList.add('opened'));
    });

    ($('.search-form') as Element | null)?.on('click', '.mob-filter-close, .btn-close', () => closePopup());

    /* Bulk WHMCS availability search */
    const renderBulkResults = (results: BulkResult[]): void => {
        elTbody.replaceChildren();
        elTable.classList.toggle('hide', !results.length);
        for (const r of results) {
            const isChild = !!r.parent;
            const statusCell = r.available
                ? El('span', { class: 'ext-status ext-status-ok',  title: 'Disponibil'  }, '✓')
                : El('span', { class: 'ext-status ext-status-no',  title: 'Indisponibil' }, '✗');
            const priceCell = r.available
                ? El('span', { class: 'price' },          `€${Number(r.register).toFixed(2)}`)
                : El('span', { class: 'price-disabled' }, `€${Number(r.register).toFixed(2)}`);
            const actionCell: HTMLElement = r.available
                ? El('a', {
                    class:  'btn-order',
                    href:   `https://client.ro/cart.php?a=add&domain=register&query=${encodeURIComponent(r.domain)}`,
                    target: '_blank',
                    rel:    'noopener',
                }, El('span', {}, 'Comandă'))
                : El('span', { class: 'btn-order-disabled' }, '—');

            elTbody.append(El('tr', { class: (isChild ? 'ext-child ' : '') + (r.available ? 'ext-avail' : 'ext-taken') },
                El('td', {}, El('span', { class: 'ext-name' }, `.${r.tld}`)),
                El('td', {}, statusCell),
                El('td', {}, '1 an'),
                El('td', {}, priceCell),
                El('td', {}, `€${Number(r.renew).toFixed(2)}`),
                El('td', {}, `€${Number(r.transfer).toFixed(2)}`),
                El('td', {}, actionCell),
            ));
        }
    };

    const bulkSearch = async (rawInput: string): Promise<void> => {
        const sld = (rawInput || '').trim().toLowerCase();
        if (!sld) return;
        rt.showLoader();
        try {
            const resp = await fetch(`/extensii-domenii/search?sld=${encodeURIComponent(sld)}`, { cache: 'no-cache' });
            const j = await resp.json() as { results?: BulkResult[]; currency?: string };
            if (!j || !Array.isArray(j.results)) {
                elTbody.replaceChildren();
                return;
            }
            renderBulkResults(j.results);
        } catch (e) {
            if (import.meta.env.DEV) console.error('bulk search err', e);
        } finally {
            rt.hideLoader();
        }
    };

    ($('.search-form') as HTMLFormElement | null)?.FormValidator({
        init: (el: Record<string, HTMLElement | HTMLElement[]>) => {
            for (const [key, val] of Object.entries(filterOptions.levels)) {
                (el['b_level'] as HTMLSelectElement).add(new Option(val, key));
            }
        },
        return_true: (fd: Record<string, unknown>) => {
            bulkSearch((fd['query'] as string) ?? '');
            return false;
        },
    });

    /* Select change handlers */
    $('.search-form select[name="categ"], .search-form select[name="b_level"]', true).on('change', function (this: Element, e: Event) {
        const t = (e.target as HTMLSelectElement);
        if (t.value && t.value !== '0') {
            renderBadge(++filtCount);
            (fdata as Record<string, unknown>)[t.name] = t.value;
        } else {
            delete (fdata as Record<string, unknown>)[t.name];
            renderBadge(--filtCount);
        }
        priceListRender();
    });

    /* Checkboxes */
    $('.search-form input[name="promo"], .search-form input[name="main_tlds"]', true).on('change', function (this: Element, e: Event) {
        const t = e.target as HTMLInputElement;
        if (t.checked) {
            renderBadge(++filtCount);
            (fdata as Record<string, unknown>)[t.name] = true;
        } else {
            delete (fdata as Record<string, unknown>)[t.name];
            renderBadge(--filtCount);
        }
        priceListRender();
    });

    /* noUiSlider price range */
    let tm: number | null = null;
    noUiSlider.create(elFeeRange, {
        start: [filterOptions.feeRange.min, filterOptions.feeRange.max],
        connect: true,
        step: 1,
        range: { min: filterOptions.feeRange.min, max: filterOptions.feeRange.max },
        format: { from: (v: string) => parseInt(v, 10), to: (v: number) => parseInt(String(v), 10) },
    }).on('update', (v: number[]) => {
        if (elFeeVal) elFeeVal.textContent = `${v[0]}€ - ${v[1]}€`;
        if (tm !== null) { clearTimeout(tm); tm = null; }
        tm = window.setTimeout(() => {
            if (v[0] === 0 && v[1] === 5000) {
                if (fdata.start_fee !== undefined || fdata.end_fee !== undefined) {
                    delete fdata.start_fee;
                    delete fdata.end_fee;
                    renderBadge(--filtCount);
                }
                priceListRender();
                return;
            }
            if (fdata.start_fee === undefined) renderBadge(++filtCount);
            fdata.start_fee = v[0];
            fdata.end_fee   = v[1];
            priceListRender();
        }, 500);
    });

    /* Radio box clicks */
    $('.search-form .radio-box span', true).on('click', function (this: Element, e: Event) {
        const target = e.target as HTMLElement;
        const rb = target.parentElement;
        if (!rb) return;
        const isActive = target.classList.contains('active');
        rb.find('span', (t: Element) => { t.classList.remove('active'); return true; });
        const rbId = rb.dataset['id'] ?? '';

        if (isActive) {
            renderBadge(--filtCount);
            target.classList.remove('active');
            delete (fdata as Record<string, unknown>)[rbId];
            priceListRender();
            return;
        }
        if (!(fdata as Record<string, unknown>)[rbId]) renderBadge(++filtCount);
        target.classList.add('active');
        (fdata as Record<string, unknown>)[rbId] = target.dataset['val'];
        priceListRender();
    });

    /* Sortable table headers */
    elTable.on('click', '.sortable', function (this: Element, e: Event) {
        const target = e.target as HTMLElement;
        const isAsc  = target.classList.contains('asc');
        const isDesc = target.classList.contains('desc');
        elTable.find('th.sortable', (t: Element) => { t.classList.remove('asc', 'desc'); return true; });
        const id = target.dataset['id'] ?? '';

        if (!isAsc && !isDesc) {
            target.className = 'sortable asc';
            fdata.sort = { [id]: 'asc' };
            priceListRender();
            return;
        }
        if (isAsc) {
            target.className = 'sortable desc';
            fdata.sort = { [id]: 'desc' };
            priceListRender();
            return;
        }
        target.className = 'sortable';
        fdata.sort = false;
        priceListRender();
    });

    /* Reset filters */
    ($('.search-form') as HTMLFormElement | null)?.on('click', '.reset-filters', () => {
        ($('.search-form') as HTMLFormElement | null)?.reset();
        $('.search-form .radio-box span', (t: Element) => { t.classList.remove('active'); return true; });
        (elFeeRange as unknown as { noUiSlider: { reset: () => void } }).noUiSlider.reset();
        filtCount = 0;
        renderBadge(0);
        fdata = {};
        priceListRender();
        closePopup();
    });

    priceListRender();
});
